import {z} from 'zod';

import {BaseVoidResponseSchema, HeaderDynamicActionsFlagColoursSchema} from '@dms/api/shared';

/**
 * Base types
 */
export const CoreExchangeMarkerSchema = z.object({
  title: z.string().nullish(),
  color: HeaderDynamicActionsFlagColoursSchema,
  key: z.string().nullish(),
});
export type CoreExchangeMarker = z.infer<typeof CoreExchangeMarkerSchema>;

export const CoreExchangeHeaderSchema = z.object({
  originEntityNumber: z.string().nullish(),
  originEntityId: z.string(),
  // Kept loose: the GET returns whatever origin type is stored, the POST enum only gates creation.
  originEntityType: z.string(),
  originEntityHeaderId: z.string().nullish(),
  customerId: z.string().nullish(),
  vehicleId: z.string().nullish(),
  returnToSupplier: z.boolean().nullish(),
  supplierId: z.string().nullish(),
  generalNote: z.string().nullish(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedBy: z.string().nullish(),
  updatedAt: z.string().nullish(),
  completedBy: z.string().nullish(),
  completedAt: z.string().nullish(),
});
export type CoreExchangeHeader = z.infer<typeof CoreExchangeHeaderSchema>;

export const CoreExchangeProcessStepSchema = z.enum([
  'DELIVERY',
  'INSPECTION',
  'DEPOSIT',
  'REQUEST',
  'AUTHORISATION',
  'RETURN',
]);
export type CoreExchangeProcessStep = z.infer<typeof CoreExchangeProcessStepSchema>;

export const CoreExchangeProcessMilestoneSchema = z.object({
  step: CoreExchangeProcessStepSchema,
  status: z.string(),
  isDone: z.boolean(),
  date: z.string().nullish(),
});
export type CoreExchangeProcessMilestone = z.infer<typeof CoreExchangeProcessMilestoneSchema>;

export const CoreExchangeItemTypeSchema = z.enum(['MAIN', 'CORE']);
export type CoreExchangeItemType = z.infer<typeof CoreExchangeItemTypeSchema>;

export const CoreExchangeItemSchema = z.object({
  coreExchangeItemId: z.string(),
  itemType: CoreExchangeItemTypeSchema,
  coreItemState: z.string().nullish(),
  itemOriginEntityId: z.string(),
  relatedItemId: z.string().nullish(),
  articleId: z.string(),
  catalogNo: z.string().nullish(),
  name: z.string().nullish(),
  quantity: z.number(),
  priceWithoutVat: z.number().nullish(),
  priceWithVat: z.number().nullish(),
  valueWithoutVat: z.number().nullish(),
  valueWithVat: z.number().nullish(),
  vatType: z.string().nullish(),
  warehouseId: z.string().nullish(),
  supplierId: z.string().nullish(),
  handlingUnitId: z.string().nullish(),
  isChangeable: z.boolean().nullish(),
});
export type CoreExchangeItem = z.infer<typeof CoreExchangeItemSchema>;

export const CoreExchangeItemsTabSchema = z.object({
  totalValueExclCoreChargeWithoutVat: z.number().nullish(),
  totalValueExclCoreChargeWithVat: z.number().nullish(),
  items: z.array(CoreExchangeItemSchema).nullish(),
  general: z
    .object({
      generalNote: z.string().nullish(),
      returnCoreItemToSupplier: z.boolean().nullish(),
      supplierId: z.string().nullish(),
    })
    .nullish(),
});
export type CoreExchangeItemsTab = z.infer<typeof CoreExchangeItemsTabSchema>;

export const CoreExchangeStorageCoreItemSchema = z.object({
  coreExchangeItemId: z.string(),
  articleId: z.string().nullish(),
  catalogNo: z.string().nullish(),
  name: z.string().nullish(),
  coreItemState: z.string().nullish(),
  coreDeliveryByCustomer: z
    .object({
      storageState: z.string().nullish(),
      deliveryDate: z.string().nullish(),
      receivedBy: z.string().nullish(),
      storageNote: z.string().nullish(),
    })
    .nullish(),
  coreInspection: z
    .object({
      inspectedDate: z.string().nullish(),
      inspectedBy: z.string().nullish(),
      additionalIdentifier: z.string().nullish(),
      coreInspectionNote: z.string().nullish(),
    })
    .nullish(),
  warehouseStorage: z
    .object({
      receivedAt: z.string().nullish(),
      receivedBy: z.string().nullish(),
      warehouseId: z.string().nullish(),
      warehouseStorageNote: z.string().nullish(),
      receiptNumber: z.string().nullish(),
      articleId: z.string().nullish(),
      articleNumber: z.string().nullish(),
      receiveNoteId: z.string().nullish(),
    })
    .nullish(),
});
export type CoreExchangeStorageCoreItem = z.infer<typeof CoreExchangeStorageCoreItemSchema>;

export const CoreExchangeStorageTabSchema = z.object({
  coreItems: z.array(CoreExchangeStorageCoreItemSchema).nullish(),
});
export type CoreExchangeStorageTab = z.infer<typeof CoreExchangeStorageTabSchema>;

export const CoreExchangeReturnToSupplierTabSchema = z.object({
  settings: z
    .object({
      returnDeadline: z.string().nullish(),
      returnTransactionType: z.string().nullish(),
      returnDiscount: z.number().nullish(),
      supplierDiscountGroupCode: z.string().nullish(),
      returnIsVehicleRequired: z.boolean().nullish(),
      returnProcessingFee: z.number().nullish(),
      returnUnitPrice: z.number().nullish(),
      returnVatRate: z.string().nullish(),
      returnVatValue: z.number().nullish(),
      returnTotalValueWithoutVat: z.number().nullish(),
      returnTotalValueWithVat: z.number().nullish(),
    })
    .nullish(),
  authorizationForReturn: z
    .object({
      rmaRequestDate: z.string().nullish(),
      rmaResponseDate: z.string().nullish(),
      rmaNumber: z.string().nullish(),
      deliveryNumber: z.string().nullish(),
      orderNumber: z.string().nullish(),
      rmaNote: z.string().nullish(),
    })
    .nullish(),
  returnExecution: z
    .object({
      returnedDate: z.string().nullish(),
      returnedBy: z.string().nullish(),
      documentNumber: z.string().nullish(),
      returnOriginDocId: z.string().nullish(),
      returnOriginDocType: z.string().nullish(),
      invoiceNumber: z.string().nullish(),
      invoiceIssueDate: z.string().nullish(),
      isInvoiced: z.boolean().nullish(),
    })
    .nullish(),
});
export type CoreExchangeReturnToSupplierTab = z.infer<typeof CoreExchangeReturnToSupplierTabSchema>;

/**
 * GET requests
 */
export const GetCoreExchangeRequestSchema = z.object({
  coreExchangeId: z.string(),
});
export type GetCoreExchangeRequest = z.infer<typeof GetCoreExchangeRequestSchema>;

export const GetCoreExchangeResponseSchema = z.object({
  coreExchangeId: z.string(),
  state: z.string(),
  markers: z.array(CoreExchangeMarkerSchema).nullish(),
  header: CoreExchangeHeaderSchema,
  processMilestones: z.array(CoreExchangeProcessMilestoneSchema).nullish(),
  tabs: z.object({
    items: CoreExchangeItemsTabSchema,
    storage: CoreExchangeStorageTabSchema.nullish(),
    returnToSupplier: CoreExchangeReturnToSupplierTabSchema.nullish(),
  }),
});
export type GetCoreExchangeResponse = z.infer<typeof GetCoreExchangeResponseSchema>;

/**
 * POST requests
 */
export const CoreExchangeOriginEntityTypeSchema = z.enum(['direct-sale', 'service-case-order']);
export type CoreExchangeOriginEntityType = z.infer<typeof CoreExchangeOriginEntityTypeSchema>;

export const PostCoreExchangeRequestSchema = z.object({
  originEntityId: z.string(),
  originEntityType: CoreExchangeOriginEntityTypeSchema,
  items: z.array(
    z.object({
      itemOriginEntityId: z.string(),
      itemType: CoreExchangeItemTypeSchema,
    })
  ),
});
export type PostCoreExchangeRequest = z.infer<typeof PostCoreExchangeRequestSchema>;

export const PostCoreExchangeResponseSchema = z.object({
  coreExchangeId: z.string(),
  items: z
    .array(
      z.object({
        coreExchangeItemId: z.string().nullish(),
      })
    )
    .nullish(),
});
export type PostCoreExchangeResponse = z.infer<typeof PostCoreExchangeResponseSchema>;

/**
 * PATCH requests — one per core exchange action dialog
 */
const CoreExchangeIdSchema = z.object({coreExchangeId: z.string()});

export const PatchCoreExchangeResponseSchema = BaseVoidResponseSchema;
export type PatchCoreExchangeResponse = z.infer<typeof PatchCoreExchangeResponseSchema>;

export const PatchCoreExchangeGeneralSettingsRequestSchema = CoreExchangeIdSchema.extend({
  body: z.object({
    returnCoreItemToSupplier: z.boolean(),
    supplierId: z.string(),
    generalNote: z.string(),
  }),
});
export type PatchCoreExchangeGeneralSettingsRequest = z.infer<
  typeof PatchCoreExchangeGeneralSettingsRequestSchema
>;

export const PatchCoreExchangeCancelDeliveryRequestSchema = CoreExchangeIdSchema.extend({
  body: z.object({storageNote: z.string()}),
});
export type PatchCoreExchangeCancelDeliveryRequest = z.infer<
  typeof PatchCoreExchangeCancelDeliveryRequestSchema
>;

export const PatchCoreExchangeCustomerDeliveryRequestSchema = CoreExchangeIdSchema.extend({
  body: z.object({
    deliveryDate: z.string(),
    receivedBy: z.string(),
    storageNote: z.string(),
  }),
});
export type PatchCoreExchangeCustomerDeliveryRequest = z.infer<
  typeof PatchCoreExchangeCustomerDeliveryRequestSchema
>;

export const CoreExchangeInspectionResultSchema = z.enum(['PASSED', 'FAILED']);
export type CoreExchangeInspectionResult = z.infer<typeof CoreExchangeInspectionResultSchema>;

export const PatchCoreExchangeInspectionRequestSchema = CoreExchangeIdSchema.extend({
  body: z.object({
    inspectedDate: z.string(),
    inspectedBy: z.string(),
    additionalIdentifier: z.string(),
    coreInspectionNote: z.string(),
    inspectionResult: CoreExchangeInspectionResultSchema,
  }),
});
export type PatchCoreExchangeInspectionRequest = z.infer<
  typeof PatchCoreExchangeInspectionRequestSchema
>;

export const PatchCoreExchangeWarehouseReceiptRequestSchema = CoreExchangeIdSchema.extend({
  body: z.object({warehouseStorageNote: z.string()}),
});
export type PatchCoreExchangeWarehouseReceiptRequest = z.infer<
  typeof PatchCoreExchangeWarehouseReceiptRequestSchema
>;

export const PatchCoreExchangeRmaRequestRequestSchema = CoreExchangeIdSchema;
export type PatchCoreExchangeRmaRequestRequest = z.infer<
  typeof PatchCoreExchangeRmaRequestRequestSchema
>;

export const PatchCoreExchangeRmaAuthorizationRequestSchema = CoreExchangeIdSchema.extend({
  body: z.object({
    rmaResponseDate: z.string(),
    rmaNumber: z.string(),
    deliveryNumber: z.string(),
    orderNumber: z.string(),
    rmaNote: z.string(),
  }),
});
export type PatchCoreExchangeRmaAuthorizationRequest = z.infer<
  typeof PatchCoreExchangeRmaAuthorizationRequestSchema
>;

export const PatchCoreExchangeRmaDenialRequestSchema = CoreExchangeIdSchema.extend({
  body: z.object({
    rmaResponseDate: z.string(),
    rmaNote: z.string().optional(),
  }),
});
export type PatchCoreExchangeRmaDenialRequest = z.infer<
  typeof PatchCoreExchangeRmaDenialRequestSchema
>;

export const PatchCoreExchangeSettingsRequestSchema = CoreExchangeIdSchema.extend({
  body: z.object({
    returnDeadline: z.string(),
    returnTransactionType: z.string(),
    returnIsVehicleRequired: z.boolean(),
    returnProcessingFee: z.number(),
    returnUnitPrice: z.number(),
    returnVatRate: z.string(),
    returnDiscount: z.number(),
    returnVatValue: z.number(),
    // Spelled as in the backend contract.
    returnSupplierDicountGroupCode: z.string(),
    returnTotalValueWithoutVat: z.number(),
    returnTotalValueWithVat: z.number(),
  }),
});
export type PatchCoreExchangeSettingsRequest = z.infer<
  typeof PatchCoreExchangeSettingsRequestSchema
>;
